/**
 * @fileoverview `<SmartLink>` — renders ANY destination correctly (LIFE-SHORTLISTED-001 P9.1).
 *
 * internal route → react-router `<Link>` (no full-page reload) · external → `<a>` with a safe `rel`
 * (and a new tab only on request, announced to screen readers) · mailto / tel / #anchor → a plain
 * `<a>` · anything else → the children as TEXT, never a link (tenant data is untrusted; a renderer is
 * tolerant, never dangerous). Replaces the per-component "is this internal?" copies and the tenant
 * site's menus, which sent every CMS URL — external ones included — to `<Link to>`.
 *
 * `onClick` fires for EVERY real link — a route, an anchor, one that leaves the site — for a caller
 * that reports its own clicks (a hero's call-to-action analytics); an unsafe destination renders as
 * text, so it has none. `onOutboundClick(href, destinationKind)` fires for a destination that LEAVES the site (web, email, phone): the
 * consumer reports it through its own analytics (`outbound_clicked`), so this package stays
 * independent of which analytics provider a site mounts.
 *
 * @module @puffin-core/marketing-shell/links/SmartLink
 */

import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { HREF_KIND, OUTBOUND_KINDS, classifyHref } from '@puffin-core/block-schema/href';

import { outboundDestinationKind } from './destinationKind.js';
import { buildLinkRel } from './linkRel.js';

/**
 * @param {Object} props
 * @param {string} props.href
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @param {boolean} [props.sponsored=false] - an affiliate / paid destination
 * @param {boolean} [props.nofollow=false]
 * @param {boolean} [props.newTab=false] - external only
 * @param {string} [props.newTabLabel] - screen-reader notice for a new tab; required with `newTab`
 * @param {string} [props.destinationKind] - what an OUTBOUND destination is for, when its author knows
 *   (`shop`, `article`, …); otherwise it is read from the scheme and host (`outboundDestinationKind`)
 * @param {(href: string, destinationKind: string) => void} [props.onOutboundClick]
 * @param {Function} [props.onClick] - any click on a real link
 * @returns {React.JSX.Element}
 */
export function SmartLink({
  href,
  children,
  className,
  sponsored = false,
  nofollow = false,
  newTab = false,
  newTabLabel,
  destinationKind,
  onOutboundClick,
  onClick,
}) {
  const kind = classifyHref(href);
  if (kind === HREF_KIND.INVALID) {
    return <span className={className}>{children}</span>;
  }
  if (kind === HREF_KIND.INTERNAL) {
    return (
      <Link
        to={href}
        className={className}
        rel={buildLinkRel({ external: false, sponsored })}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }
  const external = kind === HREF_KIND.EXTERNAL;
  const opensTab = external && newTab;
  const reportsOutbound = Boolean(onOutboundClick) && OUTBOUND_KINDS.includes(kind);
  const handleClick =
    onClick || reportsOutbound
      ? (event) => {
          onClick?.(event);
          if (reportsOutbound) {
            onOutboundClick(href, outboundDestinationKind(href, destinationKind));
          }
        }
      : undefined;
  return (
    <a
      href={href}
      className={className}
      rel={buildLinkRel({ external, sponsored, nofollow })}
      target={opensTab ? '_blank' : undefined}
      onClick={handleClick}
    >
      {children}
      {opensTab ? <span className="sr-only"> {newTabLabel}</span> : null}
    </a>
  );
}

SmartLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  sponsored: PropTypes.bool,
  nofollow: PropTypes.bool,
  newTab: PropTypes.bool,
  newTabLabel: PropTypes.string,
  destinationKind: PropTypes.string,
  onOutboundClick: PropTypes.func,
  onClick: PropTypes.func,
};

export default SmartLink;
